import React, { useEffect, useState, useRef } from "react";

// MUI
import { Form, InputGroup } from "react-bootstrap";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SendIcon from "@mui/icons-material/Send";

// Hooks
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux/hooks/redux-hooks";

// Types
import { ProjectCommentModel } from "../../../models/ProjectCommentModel";

// Actions
import { setParticularProjectParticularComment } from "../../../redux/actions/ProjectCommentActions";

// Utils
import { displayDateFormart, getAbbreviation } from "../../../utility/utils";
import {
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { AccountCircle } from "@mui/icons-material";

type CommentProps = {
  comment: ProjectCommentModel;
};

const Comment = ({ comment }: CommentProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        marginTop: "10px",
        paddingRight: "5px",
      }}
    >
      <Avatar sx={{ bgcolor: "", m: "0.2rem 0.4rem 0 0" }}>
        {getAbbreviation(comment?.commenterName)}
      </Avatar>

      <Box width="100%">
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1">{`${comment?.commenterName}`}</Typography>
          <Typography variant="subtitle2">
            {`${displayDateFormart(comment?.creationDate)}`}
          </Typography>
        </Stack>
        <Typography variant="body2"> {comment?.commentText} </Typography>
      </Box>
    </Box>
  );
};

const CommentsSection = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const particularProjectAllComments = useAppSelector(
    (state) => state.projectComments.particularProject_AllComments
  );
  const particularProjectSP = useAppSelector(
    (state) => state.projectSP.particular_ProjectSP
  );
  const loggedInUser = useAppSelector((state) => state.user.loggedIn_User);
  const [commentText, setCommentText] = useState<string>("");
  const [isDialogOpen, setDialogOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dialogPosition, setDialogPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const allUsers = useAppSelector((state) => state.user.all_Users);

  let newUsers: { [key: string]: string } = {};
  allUsers.forEach((each) => {
    let userName = `${each.firstName}${each.lastName}`;
    userName = userName.replace(/[" "]/, "");
    newUsers[userName] = each.emailId;
  });

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [particularProjectAllComments]);

  const handleCommentSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    let emailArray: string[] = [];
    if (commentText) {
      let result = commentText
        .split(/[" ",]/)
        .filter((each) => each.startsWith("@"));
      result.forEach((eachName) => {
        if (newUsers.hasOwnProperty(eachName.slice(1))) {
          emailArray.push(newUsers[eachName.slice(1)]);
        }
      });

      const commentModel: ProjectCommentModel = {
        projectCommentId: 0,
        projectId: particularProjectSP.projectId,
        creationDate: new Date(),
        commenterName: loggedInUser.fullName,
        commentText: commentText,
        commenterUserId: loggedInUser.userId,
        mentionedEmails: emailArray,
      };
      dispatch(setParticularProjectParticularComment(commentModel));
      setCommentText("");
    }
  };

  const handleCommentText = (
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const comment = e.currentTarget.value;
    setCommentText(comment);
    if (comment.length > 0) {
      const mentionMatches = comment[comment.length - 1].match(/@/);
      if (mentionMatches) {
        if (inputRef.current) {
          const inputPosition = inputRef.current.getBoundingClientRect();
          setDialogPosition({
            top: inputPosition.top - 300,
            left: inputPosition.left,
          });
          setDialogOpen(true);
        }
      } else {
        setCommentText(comment);
        setDialogOpen(false);
      }
    }
  };

  const handleMemberClick = (member: any) => {
    let memberUserName = `${member.firstName}${member.lastName}`;
    memberUserName = memberUserName.replace(/[" "]/, "");
    setCommentText((prevText) => {
      return `${prevText}${memberUserName} `;
    });
    setDialogOpen(false);
  };
  return (
    <>
      <Card
        sx={{
          margin: "5px",
          padding: "10px 10px 0",
          height: "100%",
          maxHeight: "200px",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "16px",
            lineHeight: "24px",
            color: "#312F2F",
          }}
        >
          Comments
        </Typography>
        {particularProjectAllComments.length === 0 ? (
          <Typography variant="body2" mt={2}>
            No Comments Found
          </Typography>
        ) : (
          <Box
            ref={containerRef}
            sx={{
              maxHeight: "120px",
              overflowY: "auto",
              width: "100%",
            }}
          >
            {particularProjectAllComments.map((projectComment) => (
              <Comment
                key={projectComment.projectCommentId}
                comment={projectComment}
              />
            ))}
          </Box>
        )}
        {loggedInUser.roleId !== 6 ? (
          <Form onSubmit={handleCommentSubmit}>
            <InputGroup>
              <Form.Control
                className="col-md-10 shadow-none"
                placeholder="Enter your comment"
                aria-label="Enter your comment"
                aria-describedby="basic-addon2"
                onChange={handleCommentText}
                value={commentText}
                size="sm"
                required
                ref={inputRef}
              />
              <Button
                className="col-md-1"
                variant="outlined"
                id="button-addon2"
                type="submit"
                endIcon={<SendIcon />}
              />
            </InputGroup>
          </Form>
        ) : null}
      </Card>
      <Dialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        style={{
          position: "absolute",
          top: dialogPosition?.top,
          left: dialogPosition?.left,
        }}
        hideBackdrop={true}
      >
        <DialogTitle>Members</DialogTitle>
        <List sx={{ pt: 0 }}>
          {allUsers.map((user) => (
            <ListItem key={user?.userId}>
              <ListItemButton onClick={() => handleMemberClick(user)}>
                <ListItemAvatar>
                  <Avatar>
                    <AccountCircle />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${user.firstName}${user.lastName}`} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Dialog>
    </>
  );
};

export default CommentsSection;
